import { useState } from 'react';
import { Form, TextField } from '@customerio/pluma-components/react';
import { billing } from '@/services/billing';
import { ORDWAY_AI_CREDITS_RAIL } from '@/services/billing/ordway/credits-purchase';
import { readInputValue } from '@/utils/dom';
import { AI_CREDITS_CONFIG as config, parseQuantityInput } from '../utils/credits-purchase-config';
import type { CreditsPurchasePageName } from '../utils/credits-purchase-page-name';
import { OrdwayPurchaseFormBody } from './ordway-purchase-form-body';
import * as styles from './credits-purchase.css';
import { useBillingEditGuard } from '../hooks/use-billing-edit-guard';
import { useOrdwayCreditsPurchase, type OrdwayPurchaseAttempt } from '../hooks/use-ordway-credits-purchase';

export type AiCreditsOrdwayPurchaseFormProps = {
  /** The surface that opened the form, reported with every event it fires. */
  pageName: CreditsPurchasePageName;
  onSuccess: (info: { savedCard: boolean }) => void;
  onCancel: () => void;
  onProcessingChange?: (isProcessing: boolean) => void;
};

/**
 * The AI-credits purchase form for Ordway accounts: a pack-count input, no card fields, and the
 * shared Ordway purchase run from {@link useOrdwayCreditsPurchase}, which owns the analytics funnel,
 * the status poll, and every charge-safety rule.
 */
export function AiCreditsOrdwayPurchaseForm({
  pageName,
  onSuccess,
  onCancel,
  onProcessingChange,
}: AiCreditsOrdwayPurchaseFormProps) {
  const editGuard = useBillingEditGuard();
  const [quantity, setQuantity] = useState(config.defaultQuantity);

  const purchase = useOrdwayCreditsPurchase({
    config,
    pageName,
    rail: ORDWAY_AI_CREDITS_RAIL,
    startPurchase: (packs) => billing.createAICreditsOrdwayPurchase(packs),
    poll: billing.pollForOrdwayAICreditsPurchaseTask,
    initialQuantity: config.defaultQuantity,
    onSuccess,
    onProcessingChange,
  });

  const attempt: OrdwayPurchaseAttempt = {
    quantity,
    totalPriceCents: quantity * config.pricePerUnitCents,
    totalCredits: quantity * (config.creditsPerUnit ?? 0),
  };
  const quantityError = quantityRangeError(quantity);
  const isSubmitDisabled =
    editGuard.isDisabled || quantityError != null || purchase.isSubmitting || purchase.isChargeUnverified;
  const submitDisabledMessage = submitDisabledReason(
    purchase.chargeUnverifiedMessage,
    editGuard.disabledMessage,
    quantityError,
  );

  const handleQuantityChange = (event: Event) => {
    const value = parseQuantityInput(readInputValue(event));
    if (value == null) {
      return;
    }
    setQuantity(value);
  };

  const runSubmit = async () => {
    if (isSubmitDisabled) {
      return;
    }
    await purchase.submit(attempt);
  };

  return (
    <Form autoLoading={false} onSubmit={() => void runSubmit()} data-test-ai-credits-ordway-purchase-form>
      <OrdwayPurchaseFormBody
        sectionHeading={config.quantitySectionHeading}
        totalPriceCents={attempt.totalPriceCents}
        totalCredits={attempt.totalCredits}
        unitLabel={config.creditsUnitLabel ?? 'credits'}
        disclaimer={`This purchase will be billed to your account. ${config.disclaimer}`}
        submitLabel={config.submitLabel ?? 'Buy credits'}
        isSubmitDisabled={isSubmitDisabled}
        submitDisabledMessage={submitDisabledMessage}
        isSubmitting={purchase.isSubmitting}
        onCancel={onCancel}
      >
        <TextField
          data-test-credits-quantity-input
          className={styles.numberInput}
          label={config.quantityLabel}
          type="number"
          value={String(quantity)}
          isDisabled={purchase.isSubmitting}
          isInvalid={quantityError != null}
          error={quantityError}
          min={config.minQuantity}
          max={config.maxQuantity}
          step={1}
          onInput={handleQuantityChange}
          onBlur={() => purchase.trackQuantityChanged(attempt)}
        />
      </OrdwayPurchaseFormBody>
    </Form>
  );
}

/**
 * Why submit is blocked, most binding first. An unverified charge outranks the edit guard and the
 * quantity error because it is the only one the customer cannot clear by editing the form.
 */
function submitDisabledReason(
  chargeUnverifiedMessage: string,
  editGuardMessage: string,
  quantityError: string | undefined,
): string {
  if (chargeUnverifiedMessage !== '') {
    return chargeUnverifiedMessage;
  }
  if (editGuardMessage !== '') {
    return editGuardMessage;
  }
  return quantityError ?? '';
}

// The quantity must sit within the config's min/max; returns the matching error copy or undefined.
function quantityRangeError(quantity: number): string | undefined {
  if (quantity < config.minQuantity) {
    return `Minimum ${config.quantityErrorLabel} is ${config.minQuantity}.`;
  }
  if (quantity > config.maxQuantity) {
    return `Maximum ${config.quantityErrorLabel} is ${config.maxQuantity}.`;
  }
  return undefined;
}
